import React, { useState, useEffect } from 'react';
import './CreateAccountBroker.scss';

import { StepBarContent } from '../../../../features/onboarding/components';
import { FooterContent } from '../../../../features/onboarding/components';
import { OptionDropdownInner, TextInput, DropdownOwnerships } from '../../../../shared/components/ui/atoms';
import PasswordInput from '../../../../shared/components/ui/atoms/TextInput/PasswordInput';
import { AddressCaption, formatPhoneNumber } from '../../../../features/borrower/components';
import { InputAutocompleteAddress } from '../../../../shared/components/ui/atoms';
import { useBorrower } from '../../../borrower/context/BorrowerContext';
import { useOwnership } from '../../../../features/onboarding/context/OwnershipContext';

import { getDisabledStateCodes, SPECIAL_CHARACTERS_REGEX } from '../../../../utils/constants';
import { useCheckBorrowerEmailExists } from '../../hooks/useBrokerQueries';
import { useAuth } from '../../../../features/auth/context/AuthContext';
import { formatAddressWithoutCountry } from '../../../../utils/functions';

const options = [
  {
    value: "client",
    label: "My client",
    // Ensure AntD uses this as the visible text (optionLabelProp='displayLabel')
    displayLabel: "My client",
    disabled: false,
    className: undefined
  }
]

interface CreateAccountBrokerProps {
  onSubmission: (data: any) => void;
  values: any;
}

export const CreateAccountBroker = (props: CreateAccountBrokerProps) => {
  /* Props */
  const { onSubmission, values } = props;

  /* User Data */
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState('')
  const [passwordVerification, setPasswordVerification] = useState('')


  const { checkEmail, emailExists, loading: emailCheckLoading } = useCheckBorrowerEmailExists();
  const { isBroker, isAuthenticated } = useAuth();
  const isLoggedInBroker = isBroker && isAuthenticated;
  const { ownershipsFiltered } = useBorrower();
  const { ownershipSelected, setOwnershipSelected } = useOwnership();

  const [borrowerFirstName, setBorrowerFirstName] = useState("")
  const [borrowerLastName, setBorrowerLastName] = useState("")
  const [investmentAddress, setInvestmentAddress] = useState("")
  const [investmentAddressFormatted, setInvestmentAddressFormatted] = useState('')
  const [hasSelectedInvestmentAddress, setHasSelectedInvestmentAddress] = useState(false)
  const [hasManuallyEditedInvestmentAddress, setHasManuallyEditedInvestmentAddress] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [borrowerErrors, setBorrowerErrors] = useState<{ [key: string]: string }>({})

  const createOnChange = (fun: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => fun(e.target.value);


  const [emailCheckTimeout, setEmailCheckTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  const checkEmailExists = async (emailValue: string) => {
    if (!emailValue || !emailValue.includes('@')) {
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
      return;
    }

    setEmailValidationState(prev => ({ ...prev, isChecking: true }));

    try {
      await checkEmail({ variables: { email: emailValue } });
      // The emailExists value will be updated by the useEffect below
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
    }
  };

  const debouncedEmailCheck = (emailValue: string) => {
    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
    }

    const timeout = setTimeout(() => {
      checkEmailExists(emailValue.trim().toLowerCase());
    }, 500); // 500ms debounce

    setEmailCheckTimeout(timeout);
  };

  // Sync borrower first/last name when an ownership is selected from DropdownOwnerships
  useEffect(() => {
    if (!ownershipSelected) {
      return;
    }

    if (ownershipSelected.isNew) {
      // New guarantor: clear inputs so broker can type a fresh borrower
      setBorrowerFirstName("");
      setBorrowerLastName("");
      return;
    }

    const name = typeof ownershipSelected.name === 'string' ? ownershipSelected.name.trim() : '';
    const lastName = typeof ownershipSelected.lastname === 'string' ? ownershipSelected.lastname.trim() : '';

    if (name || lastName) {
      setBorrowerFirstName(name);
      setBorrowerLastName(lastName);
    }
  }, [ownershipSelected]);

  /* State - Step */
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (isLoggedInBroker) {
      setStep(1);
    }
  }, [isLoggedInBroker]);

  const firstStepCompleted = (
    true && firstName && lastName && email && phoneNumber && password && passwordVerification
  )


  const updateStep = (newStep: number) => {
    if (isLoggedInBroker) {
      return;
    }
    if (step === 0 && !firstStepCompleted) {
      return
    } else {
      setStep(newStep)
    }
  }

  const nextStep = () => {
    updateStep(step + 1)
  }

  /* Markup */
  const firstStepOnClick = () => {
    nextStep()
  }

  const stepOptions = isLoggedInBroker ? ["Borrower Information"] : ["Broker Information", "Borrower Information"];
  const displayedStepIndex = isLoggedInBroker ? 0 : step;

  const stepBarMarkup = (
    <StepBarContent
      selectedStep={displayedStepIndex}
      setSelectedStep={updateStep}
      options={stepOptions}
    />
  )

  const [emailValidationState, setEmailValidationState] = useState({
    isChecking: false,
    hasBeenChecked: false,
    exists: false
  });

  const brokerData = {
    firstName,
    lastName,
    email,
    phoneNumber,
    password,
    passwordVerification
  }

  const isFormBrokerComplete = () => {
    return Object.values(brokerData).every(value => value !== undefined && value !== '');
  }

  const validatePasswordRequirements = (password: string) => {
    const requirements = [
      { label: "Lowercase letter", validator: (pwd: string) => /[a-z]/.test(pwd) },
      { label: "Uppercase letter", validator: (pwd: string) => /[A-Z]/.test(pwd) },
      { label: "Symbol", validator: (pwd: string) => SPECIAL_CHARACTERS_REGEX.test(pwd) },
      { label: "8 characters", validator: (pwd: string) => pwd.length >= 8 }
    ];

    return requirements.map(req => ({
      ...req,
      met: req.validator(password)
    }));
  };

  const isPasswordValid = (password: string) => {
    const requirements = validatePasswordRequirements(password);
    return requirements.every(req => req.met);
  };

  const validateInput = (label: string, value: string) => {
    switch (label) {
      case "First name":
      case "Last name":
      case "Borrower First Name":
      case "Borrower Last Name":
        return value.trim() === "" ? "This field is required" : null;
      case "Email address":
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) return "Invalid email address";
        if (emailValidationState.isChecking) return "Checking email availability...";
        if (emailValidationState.hasBeenChecked && emailValidationState.exists) {
          return "This email is already registered";
        }
        return null;
      case "Phone number":
        if (value.trim() === "") return "This field is required";
        const digits = value.replace(/\D/g, '');
        if (digits.length < 10) return "Phone number must have 10 digits";
        return null;
      case "Password":
        if (value.length === 0) return "Password is required";
        if (!isPasswordValid(value)) return "Password does not meet requirements";
        return null;
      case "Confirm Password":
        if (!password || !value) return "Please confirm your password";
        if (value !== password) return "Passwords do not match";
        return null;
      case "Investment Address":
        if (value.trim() === "") return "Investment address is required";
        if (!hasSelectedInvestmentAddress) {
          return "Please select an address from the suggestions";
        }
        return null;
      default:
        return null;
    }
  };

  const checkErrors = () => {
    const newErrors = {
      firstName: validateInput("First name", firstName) || "",
      lastName: validateInput("Last name", lastName) || "",
      email: validateInput("Email address", email) || "",
      phoneNumber: validateInput("Phone number", phoneNumber) || "",
      password: validateInput("Password", password) || "",
      passwordConfirmation: validateInput("Confirm Password", passwordVerification) || ""
    };
    setErrors(newErrors);
    return newErrors
  }

  const checkBorrowerErrors = () => {
    const newBorrowerErrors = {
      borrowerFirstName: validateInput("Borrower First Name", borrowerFirstName) || "",
      borrowerLastName: validateInput("Borrower Last Name", borrowerLastName) || "",
      investmentAddress: validateInput("Investment Address", investmentAddress) || ""
    };
    setBorrowerErrors(newBorrowerErrors);
    return newBorrowerErrors;
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors();
    }
  }, [firstName, lastName, email, phoneNumber, password, passwordVerification])

  useEffect(() => {
    if (Object.keys(borrowerErrors).length > 0) {
      checkBorrowerErrors();
    }
  }, [borrowerFirstName, borrowerLastName, investmentAddress, hasSelectedInvestmentAddress, hasManuallyEditedInvestmentAddress])

  useEffect(() => {
    if (email && email.includes('@')) {
      debouncedEmailCheck(email);
    } else {
      setEmailValidationState({
        isChecking: false,
        hasBeenChecked: false,
        exists: false
      });
    }

    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [email]);

  useEffect(() => {
    if (emailValidationState.isChecking && !emailCheckLoading) {
      setEmailValidationState(prev => ({
        ...prev,
        isChecking: false,
        hasBeenChecked: true,
        exists: emailExists
      }));
    }
  }, [emailExists, emailCheckLoading, emailValidationState.isChecking]);

  const handleContinue = () => {
    const errors = checkErrors();
    const hasErrors = Object.values(errors).some(error => Boolean(error));
    const isEmailValid = !emailValidationState.isChecking &&
      (!emailValidationState.hasBeenChecked || !emailValidationState.exists);

    if (isFormBrokerComplete() && !hasErrors && isEmailValid) {
      nextStep()
    }
  }

  const firstStepMarkup = (
    <React.Fragment>
      <div className='inner-container'>
        {stepBarMarkup}
        <h1 className='title'>
          Enter your info to offer loans to your real estate investor clients
        </h1>

        <div className='wide-input-container'>
          <TextInput label={"First name"} value={firstName} onChange={(e) => { setFirstName(e) }} error={errors.firstName} floatingLabel />
          <TextInput label={"Last name"} value={lastName} onChange={(e) => setLastName(e)} error={errors.lastName} floatingLabel />
          <TextInput label={"Email address"} value={email} onChange={(e) => setEmail(e)} error={errors.email} floatingLabel />
          <TextInput label={"Phone number"} value={phoneNumber} onChange={(e) => setPhoneNumber(formatPhoneNumber(e))} error={errors.phoneNumber} floatingLabel />
          <PasswordInput
            label={"Password"}
            value={password}
            onChange={(e) => setPassword(e)}
            error={errors.password}
            showRequirements={true}
            wrapperClassName="requirements-desktop"
            floatingLabel
          />
          <PasswordInput
            label={"Confirm Password"}
            value={passwordVerification}
            onChange={(e) => setPasswordVerification(e)}
            error={errors.passwordConfirmation}
            showRequirements={true}
            requirementsValue={password}
            wrapperClassName="requirements-mobile"
            floatingLabel
          />
        </div>
      </div>

      <div className='resume-container'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerMode={true} isBrokerResume={true} />
      </div>

      <div className='loan-options-button' onClick={handleContinue}>
        Go to borrower info
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  const isBorrowerFormComplete = () => {
    return borrowerFirstName && borrowerLastName && investmentAddress;
  }

  const secondStepOnClick = () => {
    const borrowerErrors = checkBorrowerErrors();
    const hasBorrowerErrors = Object.values(borrowerErrors).some(error => Boolean(error));

    if (isBorrowerFormComplete() && !hasBorrowerErrors && hasSelectedInvestmentAddress) {
      const userData = {
        broker: {
          firstName, lastName, email, phoneNumber, password
        },
        borrower: {
          firstName: borrowerFirstName,
          lastName: borrowerLastName,
          investmentAddress: investmentAddressFormatted || investmentAddress
        }
      }
      onSubmission(userData)
    } else {
      // Force validation to show errors if form is incomplete
      checkBorrowerErrors();
    }
  }

  const secondStepMarkup = (
    <React.Fragment>
      <div className='padding-container'>
        {stepBarMarkup}
        <h1 className='title'>
          Enter borrower info to get a term sheet and pre-approval letter
        </h1>

        <p className='description'>
          {isLoggedInBroker ? (
            <>
              <DropdownOwnerships
                items={ownershipsFiltered || []}
                disabled={false}
                // For this flow, show "My client" when the broker chooses NEW BORROWER
                guarantorFirstName="My"
                guarantorLastName="client"
                newItemLabel="NEW BORROWER"
              />{' '}
              is the borrower
            </>
          ) : (
            <>
              <OptionDropdownInner
                defaultValue={options[0].value}
                options={options}
                customWidth={"200px"}
                isBrokerMode={true}
                onChange={() => { }}
              />{' '}
              is the borrower
            </>
          )}
        </p>

        <div className='input-container'>
          <TextInput
            label='First Name'
            value={borrowerFirstName}
            onChange={(e) => setBorrowerFirstName(e)}
            error={borrowerErrors.borrowerFirstName}
            disabled={Boolean(ownershipSelected && !ownershipSelected.isNew)}
          />
          <TextInput
            label='Last Name'
            value={borrowerLastName}
            onChange={(e) => setBorrowerLastName(e)}
            error={borrowerErrors.borrowerLastName}
            disabled={Boolean(ownershipSelected && !ownershipSelected.isNew)}
          />
        </div>
      </div>

      <div className='resume-container first'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerFirst={true} />
      </div>

      <div className='padding-container'>
        <div className='property'>
          <InputAutocompleteAddress
            className='broker'
            value={investmentAddress}
            onChange={(address: string) => {
              setInvestmentAddress(address);
              if (address.trim().length === 0) {
                setHasSelectedInvestmentAddress(false);
                setHasManuallyEditedInvestmentAddress(false);
              } else {
                setHasSelectedInvestmentAddress(false);
                setHasManuallyEditedInvestmentAddress(true);
              }
            }}
            onSelect={(data: any) => {
              const selectedAddress = data.selectedAddress || data.formatted_address;
              const displayAddress = formatAddressWithoutCountry(selectedAddress);
              setInvestmentAddress(displayAddress);

              setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));

              setHasSelectedInvestmentAddress(true);
              setHasManuallyEditedInvestmentAddress(false);
            }}
            error={borrowerErrors.investmentAddress}
            excludedStates={getDisabledStateCodes()}
          />
        </div>
        <AddressCaption />
      </div>

      <div className='resume-container last'>
        <FooterContent values={values} oneLine={true} isSpan={true} isBrokerLast={true} />
      </div>

      <div className='loan-options-button' onClick={secondStepOnClick}>
        Get a term sheet for your client
      </div>
      <p className='caption'>
        We will NEVER sell your info.<br></br>
        By providing your phone and email, you agree to receive text messages from Constitution Lending.
      </p>
    </React.Fragment>
  )

  /* Component */
  return (
    <div className='create-account-broker broker'>
      <div className='info'>
        {
          !isLoggedInBroker && step === 0
            ?
            firstStepMarkup
            :
            secondStepMarkup
        }
      </div>
    </div>
  );
}
